import React, { useState, useCallback, useEffect, memo } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import { imageGalleryData02 } from './ImageGalleryData';

const galleryStyles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
    padding: '0',
    width: '100%',
  },
  imageWrapper: {
    position: 'relative',
    borderRadius: '14px',
    overflow: 'hidden',
    cursor: 'pointer',
    aspectRatio: '4 / 3',
    background: '#F0ECE6',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'block',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to top, rgba(26,26,46,0.5) 0%, transparent 60%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    display: 'flex',
    alignItems: 'flex-end',
    padding: '20px',
  },
  overlayTitle: {
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
  },
  zoomIcon: {
    position: 'absolute',
    top: '14px',
    right: '14px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#D4A853',
    fontSize: '14px',
    opacity: 0,
    transform: 'translateY(6px)',
    transition: 'opacity 0.25s ease, transform 0.25s ease',
  },
  lightboxOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10, 10, 20, 0.92)',
    backdropFilter: 'blur(12px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  },
  lightboxContent: {
    position: 'relative',
    maxWidth: '1100px',
    maxHeight: '85vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lightboxImage: {
    maxWidth: '100%',
    maxHeight: '82vh',
    borderRadius: '12px',
    objectFit: 'contain',
    boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
  },
  lightboxClose: {
    position: 'fixed',
    top: '20px',
    right: '24px',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#FFFFFF',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
    zIndex: 10001,
  },
  lightboxNav: {
    position: 'fixed',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#FFFFFF',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
    zIndex: 10001,
  },
  lightboxCounter: {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: 'rgba(255,255,255,0.5)',
    fontSize: '13px',
    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
    fontWeight: 500,
    zIndex: 10001,
  },
  lightboxCaption: {
    position: 'fixed',
    bottom: '48px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#FFFFFF',
    fontSize: '15px',
    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
    fontWeight: 600,
    zIndex: 10001,
    textAlign: 'center',
    maxWidth: '500px',
  },
};

const ImageGallery = (props) => {
  const { data } = props;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openLightbox = useCallback((index) => {
    setActiveIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % data.length);
  }, [data.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + data.length) % data.length);
  }, [data.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, closeLightbox, goNext, goPrev]);

  if (!data || data.length === 0) return null;

  return (
    <>
      {/* Gallery Grid */}
      <div style={galleryStyles.grid}>
        {data.map((item, i) => (
          <m.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            style={galleryStyles.imageWrapper}
            onClick={() => openLightbox(i)}
            onMouseEnter={(e) => {
              const img = e.currentTarget.querySelector('img');
              const ov = e.currentTarget.querySelector('.g-overlay');
              const ic = e.currentTarget.querySelector('.g-zoom');
              if (img) img.style.transform = 'scale(1.08)';
              if (ov) ov.style.opacity = '1';
              if (ic) { ic.style.opacity = '1'; ic.style.transform = 'translateY(0)'; }
            }}
            onMouseLeave={(e) => {
              const img = e.currentTarget.querySelector('img');
              const ov = e.currentTarget.querySelector('.g-overlay');
              const ic = e.currentTarget.querySelector('.g-zoom');
              if (img) img.style.transform = 'scale(1)';
              if (ov) ov.style.opacity = '0';
              if (ic) { ic.style.opacity = '0'; ic.style.transform = 'translateY(6px)'; }
            }}
          >
            {item.src ? (
              <img src={item.src} alt={item.title || `Gallery ${i + 1}`} style={galleryStyles.image} loading="lazy" />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D4A853', fontSize: '2rem', opacity: 0.3 }}>
                <i className="fas fa-image"></i>
              </div>
            )}
            <div className="g-overlay" style={galleryStyles.overlay}>
              {item.title && <span style={galleryStyles.overlayTitle}>{item.title}</span>}
            </div>
            <div className="g-zoom" style={galleryStyles.zoomIcon}>
              <i className="fas fa-expand"></i>
            </div>
          </m.div>
        ))}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && data[activeIndex] && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={galleryStyles.lightboxOverlay}
            onClick={closeLightbox}
          >
            <button
              style={galleryStyles.lightboxClose}
              onClick={closeLightbox}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
            >
              <i className="fas fa-times"></i>
            </button>

            {data.length > 1 && (
              <button
                style={{ ...galleryStyles.lightboxNav, left: '20px' }}
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
            )}

            <m.div
              key={activeIndex}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.25 }}
              style={galleryStyles.lightboxContent}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={data[activeIndex].src}
                alt={data[activeIndex].title || ''}
                style={galleryStyles.lightboxImage}
              />
            </m.div>

            {data.length > 1 && (
              <button
                style={{ ...galleryStyles.lightboxNav, right: '20px' }}
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            )}

            {data[activeIndex].title && (
              <div style={galleryStyles.lightboxCaption}>{data[activeIndex].title}</div>
            )}

            <div style={galleryStyles.lightboxCounter}>
              {activeIndex + 1} / {data.length}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
};

ImageGallery.defaultProps = {
  data: imageGalleryData02,
  animationDelay: 0.2
};

ImageGallery.propTypes = {
  data: PropTypes.array,
  animation: PropTypes.object,
  animationDelay: PropTypes.number,
  theme: PropTypes.string,
  grid: PropTypes.string,
  overlay: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default memo(ImageGallery);
